/* eslint-disable global-require */
// Internally, it uses whatwg-fetch polyfill on the client, and node-fetch on
// the server, Be aware that any fetch polyfill assumes a Promise polyfill i
// is already present. The easiest way to ensure you have a Promise polyfill is
// to enable Babel's ES6 polyfill in your entry point before any other code runs
// import 'babel-polyfill'; /* Deprecated package: use  core-js & regenerator-runtime instead */
// backend mock
import AppUpdate from '@components/AppUpdate';
import SingletonTab from '@components/SingletonSession';
import 'core-js/stable';
// Required by MaterialUI
import MuiThemeProvider from 'material-ui/styles/MuiThemeProvider';
import { SnackbarProvider } from 'notistack';
import { createRoot } from 'react-dom/client';
import { RawIntlProvider } from 'react-intl';
// i18n via React Intl
import { Provider } from 'react-redux';
// React Router is used to implement routes
import { ErrorInfo, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { BrowserRouter, Route, Router, Switch } from 'react-router-dom';
import 'regenerator-runtime/runtime';
import { appUpdateFound, appUpdateRedundant } from './reducers/appUpdate';
// Error box component
// React component to asynchronously load other components dependending on routes (code-splitting)
import asyncComponent from './components/AsyncComponent/AsyncComponent';
import { BrowserDetect } from './components/BrowserDetect';
import LogDisplay from './components/LogDisplay';
import RedirectRoute from './components/RedirectRoute';
import './index.css';
import intl, { intlPolyfill } from './locales/intl';
// AgentChatManager module
import AgentChatManager from './logic/agentchatmanager';
import BackendProxy from './logic/backendproxy';
// Global ErrorHandler module
import ErrorHandler from './logic/errorhandler';
// Theming
import muiTheme, { CustomMuiV4ThemeProvider } from './muithemes/b2ChatTheme';
import { ThemeModeProvider } from './contexts/ThemeMode';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
// Redux store
import store from './store/reduxstore';
// use Spanish datetime format globally
import globalHistory from './globalHistory';
import ErrorPage from './utils/ErrorPage';

import { DialogsProvider } from './components/Common/Dialog/DialogProvider';
import TwoFactorAuthEmailOtpReminder from './components/Login/TwoFactorAuth/TwoFactorAuthReminder';
import './utils/dayjs';
import UserActivity from './utils/UserActivity';
import UserLogoutOnCloseTab from './utils/userLogoutOnCloseTab';
import WrapperConsole from './WrapperConsole';

// service worker registration and setup on update
const configuration = {
  onUpdate() {
    store.dispatch(appUpdateFound());
  },
  onRedundant() {
    store.dispatch(appUpdateRedundant());
  },
};

serviceWorkerRegistration.register(configuration);

// Connect the AgentChatManager module to the backend through a BackendProxy module
AgentChatManager.initialize(store);

// Link the global ErrorHandler module with the Redux store, to enable it to trigger actions
ErrorHandler.initialize(store);

// Start module in charge of loading chunks of chats whenever appropriate

const asyncLogin = () => import('./components/Login');
const asyncChatViewer = () => import('./components/ChatViewer');
const asyncUserActivation = () => import('./components/UserActivation');
const asyncUserAggregator = () => import('./components/UserAggregator');
const asyncAdminHome = () => import('./components/AdminHome');
const asyncForgotPassword = () => import('./components/Password');
const asyncChangePassword = () =>
  import('./components/Password/ChangePassword');
const asyncSignUp = () => import('./components/SignUp');
const asyncUnitTestRunner = () => import('./tests/UnitTestRunner');
const asyncWspOnboarding = () => import('./components/WspOnboarding');
// Frozen pre-v2 bridge until Meta's 2026-10-15 cutoff — remove with ENG-1911.
const asyncWspOnboardingLegacy = () =>
  import('./components/WspOnboardingLegacy');
const asyncVersionInfo = () => import('./components/VersionInfo');
const asyncTwoFactorAuth = () =>
  import('./components/Login/TwoFactorAuth/TwoFactorAuthSetup');

const asyncTwoFactorAuthVerifyEmail = () =>
  import('./components/Login/TwoFactorAuth/TwoFactorAuthSetupEmailOtp');
const asyncTwoFactorAuthVerifyOTPActivation = () =>
  import('./components/Login/TwoFactorAuth/TwoFactorAuthVerifyOTPActivation');

const asyncTwoFactorAuthVerifyOTPLogin = () =>
  import('./components/Login/TwoFactorAuth/TwoFactorAuthVerifyOTPLogin');

const asyncTwoFactorAuthSuccess = () =>
  import('./components/Login/TwoFactorAuth/TwoFactorAuthSuccess');

const asyncTotpEnrollmentSetup = () =>
  import('./components/Login/TotpEnrollment/TotpEnrollmentSetup');
const asyncTotpEnrollmentConfirm = () =>
  import('./components/Login/TotpEnrollment/TotpEnrollmentConfirm');
const asyncTotpEnrollmentRecoveryCodes = () =>
  import('./components/Login/TotpEnrollment/TotpEnrollmentRecoveryCodes');
const asyncTotpMigrationNotice = () =>
  import('./components/Login/TotpEnrollment/TotpMigrationNotice');
const asyncTotpChallenge = () =>
  import('./components/Login/TotpChallenge/TotpChallenge');
const asyncTotpRecoveryLogin = () =>
  import('./components/Login/TotpChallenge/TotpRecoveryLogin');
const asyncMfaRolloutNotice = () =>
  import('./components/Login/MfaRolloutNotice/MfaRolloutNotice');

const asyncLoginRedirect = () => import('./components/Login/LoginRedirect');
const asyncOAuthCallback = () =>
  import('./components/OAuthCallback/OAuthCallback');

const RootComponent = () => (
  <RawIntlProvider value={intl}>
    <Provider store={store}>
      <MuiThemeProvider muiTheme={muiTheme}>
        <ThemeModeProvider>
          <CustomMuiV4ThemeProvider>
            <DialogsProvider>
              <SnackbarProvider
                maxSnack={5}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                preventDuplicate
              >
                <div
                  style={{
                    minHeight: '100vh',
                    height: '100vh',
                    width: '100vw',
                    overflow: 'auto',
                  }}
                >
                  <BrowserDetect />
                  <SingletonTab />
                  <LogDisplay topic="main" />
                  <UserActivity />
                  <UserLogoutOnCloseTab />

                  <ErrorBoundary
                    FallbackComponent={ErrorPage}
                    onReset={() => {
                      window.location.reload();
                    }}
                    onError={(error: Error, info: ErrorInfo) =>
                      // eslint-disable-next-line no-console
                      console.error(error.message, info)
                    }
                  >
                    <BrowserRouter>
                      <Router history={globalHistory}>
                        <Switch>
                          <Route
                            exact
                            path="/"
                            component={asyncComponent(asyncLogin)}
                          />
                          <Route
                            path="/viewer/:chatId/:merchantToken"
                            component={asyncComponent(asyncChatViewer)}
                          />
                          <Route
                            exact
                            path="/login/2fa/setup"
                            component={asyncComponent(asyncTwoFactorAuth)}
                          />
                          <Route
                            exact
                            path="/login/2fa/email-otp"
                            component={asyncComponent(
                              asyncTwoFactorAuthVerifyEmail
                            )}
                          />
                          <Route
                            exact
                            path="/login/2fa/email-otp/verify"
                            component={asyncComponent(
                              asyncTwoFactorAuthVerifyOTPActivation
                            )}
                          />
                          <Route
                            exact
                            path="/login/2fa/email-otp/success"
                            component={asyncComponent(
                              asyncTwoFactorAuthSuccess
                            )}
                          />
                          <Route
                            exact
                            path="/login/2fa/otp"
                            component={asyncComponent(
                              asyncTwoFactorAuthVerifyOTPLogin
                            )}
                          />
                          <Route
                            exact
                            path="/login/mfa/migration"
                            component={asyncComponent(asyncTotpMigrationNotice)}
                          />
                          <Route
                            exact
                            path="/login/mfa/totp/setup"
                            component={asyncComponent(asyncTotpEnrollmentSetup)}
                          />
                          <Route
                            exact
                            path="/login/mfa/totp/confirm"
                            component={asyncComponent(
                              asyncTotpEnrollmentConfirm
                            )}
                          />
                          <Route
                            exact
                            path="/login/mfa/totp/recovery-codes"
                            component={asyncComponent(
                              asyncTotpEnrollmentRecoveryCodes
                            )}
                          />
                          <Route
                            exact
                            path="/login/mfa/totp/challenge"
                            component={asyncComponent(asyncTotpChallenge)}
                          />
                          <Route
                            exact
                            path="/login/mfa/totp/recovery"
                            component={asyncComponent(asyncTotpRecoveryLogin)}
                          />
                          <Route
                            exact
                            path="/login/mfa/rollout-notice"
                            component={asyncComponent(asyncMfaRolloutNotice)}
                          />
                          <Route
                            exact
                            path="/login/redirect"
                            component={asyncComponent(asyncLoginRedirect)}
                          />
                          <Route
                            path="/login"
                            component={asyncComponent(asyncLogin)}
                          />
                          <Route
                            path="/user-activation/:token"
                            component={asyncComponent(asyncUserActivation)}
                          />
                          <Route
                            path="/config-user-aggregator"
                            component={asyncComponent(asyncUserAggregator)}
                          />
                          <Route
                            path="/forgot-password"
                            component={asyncComponent(asyncForgotPassword)}
                          />
                          <Route
                            path="/change-password/:token"
                            component={asyncComponent(asyncChangePassword)}
                          />
                          <Route
                            path="/agent/chat"
                            render={() => (
                              <Suspense fallback={<div />}>
                                <WrapperConsole />
                              </Suspense>
                            )}
                          />
                          <Route
                            path="/admin"
                            component={asyncComponent(asyncAdminHome)}
                          />
                          <Route
                            path="/signup"
                            component={asyncComponent(asyncSignUp)}
                          />
                          <Route
                            path="/unittest"
                            component={asyncComponent(asyncUnitTestRunner)}
                          />

                          <Route
                            exact
                            path="/whatsapp-onboarding"
                            component={asyncComponent(asyncWspOnboarding)}
                          />

                          <Route
                            exact
                            path="/whatsapp-onboarding-legacy"
                            component={asyncComponent(asyncWspOnboardingLegacy)}
                          />

                          <Route
                            exact
                            path="/logout"
                            render={() => {
                              BackendProxy.logOut(
                                () => {
                                  window.location.pathname = '/login';
                                },
                                () => {
                                  window.location.pathname = '/login';
                                }
                              );
                              return null;
                            }}
                          />

                          <Route
                            exact
                            path="/version"
                            component={asyncComponent(asyncVersionInfo)}
                          />

                          <Route
                            exact
                            path="/:merchantId/oauth/callback"
                            component={asyncComponent(asyncOAuthCallback)}
                          />
                          <RedirectRoute />
                        </Switch>
                        <TwoFactorAuthEmailOtpReminder />
                        <AppUpdate />
                      </Router>
                    </BrowserRouter>
                  </ErrorBoundary>
                </div>
              </SnackbarProvider>
            </DialogsProvider>
          </CustomMuiV4ThemeProvider>
        </ThemeModeProvider>
      </MuiThemeProvider>
    </Provider>
  </RawIntlProvider>
);

(async function renderRoot() {
  await intlPolyfill();
  const root = createRoot(document.getElementById('root')!);
  root.render(RootComponent());
})();

// eslint-disable-next-line no-console
console.log('VERSION', window.version);
